<template>
	<div>
		<div v-if="initialised">
			<template v-if="log">
				<div class="row">
					<div class="col-md-6">
						<h1 class="text-primary no-margin">{{ $t('veneerLog') }} {{ name }}</h1>
						<h2 class="q-mt-none q-mb-lg">{{ speciesName }}</h2>
						<p class="text-secondary" v-if="unavailable">{{ $t('logUnavailable.long') }}</p>
						<p v-if="isWatchlistItem && watchlistItem && watchlistItem.price">{{ $t('label.price') }}: {{ watchlistItem.price | currency }}</p>
						<p v-if="isWatchlistItem && watchlistItem && watchlistItem.description">{{ $t('label.comment') }}: {{ watchlistItem.description }}</p>
					</div>
					<div class="col-md-6">
						<q-btn-group spread>
							<q-btn @click="showMeasurementListDialog(log, isVirtual)"
								   :label="$t('button.measurementList')"
								   color="primary"
								   icon="img:\typo3conf\ext\ig_warehouse\Resources\Public\Icons\tally-sheet.png"
								   no-caps
								   outline
							/>
							<q-btn v-if="user"
								   @click="showWatchlistDialog(log, isVirtual)"
								   :label="$t('button.watch')"
								   color="primary"
								   icon="star_border"
								   no-caps
								   outline
							/>
							<q-btn v-if="log.uid && smallImages"
								   @click="downloadImages"
								   :label="$t('button.downloadImages')"
								   color="primary"
								   icon="cloud_download"
								   no-caps
								   outline
							/>
							<q-btn v-if="!hideBackLink && detailViewBackLink"
								   :to="detailViewBackLink"
								   :label="$t('button.back')"
								   color="primary"
								   icon="arrow_back"
								   no-caps
								   outline
							/>
						</q-btn-group>

						<q-btn v-if="previousLogLink" :to="previousLogLink" :label="$t('button.previousLog')"/>
						<q-btn v-if="nextLogLink" :to="nextLogLink" :label="$t('button.nextLog')" :loading="loadingLogs"/>
					</div>
				</div>

				<bundle-viewer v-if="showBundleViewer"
							   :log="log"
							   :height="800"
							   :zoomable="true"
							   :fullscreen="true"
							   :scala="true"
							   :isVirtual="isVirtual"
							   class="q-my-lg"
				/>

				<q-card class="q-my-md bg-grey-4" square flat>
					<veneer-log-info :log="log" :is-public="isPublic" class="q-pa-md"/>
				</q-card>

				<veneer-log-images :images="smallImages" large/>
			</template>
			<template v-else>
				Furnierblock kann nicht angezeigt werden.
			</template>
		</div>

		<q-spinner-cube v-else color="primary" size="5em" class="absolute-center"/>
	</div>
</template>

<script>
	import LogPropertiesResolver from "@/mixins/LogPropertiesResolver";
	import {DOWNLOAD_MANUAL_IMAGES, FETCH_LOG, FETCH_VIRTUAL_LOG, FETCH_WATCHLIST, FETCH_WATCHLIST_ITEM, FETCH_WATCHLIST_BY_ITEM} from "@/store/actions.type";
	import {SET_DETAIL_ITEM} from "@/store/mutations.type";
	import VeneerLogInfo from "@/components/common/VeneerLogInfo";
	import VeneerLogImages from "@/components/detail/VeneerLogImages";
	import MeasurementListDialogOpener from "@/mixins/MeasurementListDialogOpener";
	import WatchlistDialogOpener from "@/mixins/WatchlistDialogOpener";
	import {mapGetters} from "vuex";
	import BundleViewer from "@/components/detail/BundleViewer";
	import LogUtility from "@/mixins/LogUtility";
	import LogSearchMixin from "@/mixins/LogSearchMixin";
	import NumberFormattingFilters from "@/mixins/NumberFormattingFilters";
	import {SEARCH_TYPE} from "@/common/constants";

	export default {
		name: "Detail",
		components: {BundleViewer, VeneerLogImages, VeneerLogInfo},
		mixins: [LogPropertiesResolver, WatchlistDialogOpener, MeasurementListDialogOpener, LogUtility, LogSearchMixin, NumberFormattingFilters],
		props: ['propLog', 'identifier', 'hideBackLink'],
		data() {
			return {
				initialised: false,
				previousLogLink: false,
				nextLogLink: false
			}
		},
		mounted() {
			this.initView();
		},
		watch: {
			identifier() {
				this.initialised = false;
				this.initView();
			}
		},
		computed: {
			...mapGetters(['user', 'log', 'watchlist', 'watchlistItem', 'loadingLogs', 'logSearchResults', 'detailViewBackLink', 'searchType']),
			isWatchlistItem() {
				return this.$route.meta.hasOwnProperty('isWatchlistItem') && this.$route.meta.isWatchlistItem || this.isVirtual;
			},
			isVirtual() {
				return this.watchlistItem ? this.isItemVirtual(this.watchlistItem) : false;
			},
			// is public view reached through shared watchlist
			isPublic() {
				return this.$route.meta.hasOwnProperty('isPublic') && this.$route.meta.isPublic;
			},
			isDynamicWatchlistItem() {
				return this.$route.meta.hasOwnProperty('isDynamicWatchlistItem') && this.$route.meta.isDynamicWatchlistItem;
			},
			name() {
				return this.logNo || this.log.name;
			},
			logList() {
				return this.isWatchlistItem && this.watchlist ? this.watchlist.items : this.logSearchResults;
			},
			// current iterator index for this log in its respective list (either searchResult or watchlistItems)
			listIndex() {
				if (this.isWatchlistItem) {
					return this.logList.findIndex(item => item.uid === this.watchlistItem.uid);
				}
				if (this.logSearchResults) {
					return this.logList.findIndex(log => log.intLogId === this.logId);
				}

				return null;
			},
			showBundleViewer() {
				/*
					show bundle viewer on non public views or
					when showBundleImages is set to true for that watchlist and
					on a static watchlistitem showBundleImages is set to true or
					this is a dynamic watchlist
				 */
				return !this.isPublic ||
					this.watchlist.showBundleImages &&
						((this.watchlistItem && this.watchlistItem.showBundleImages) || this.isDynamicWatchlistItem);
			}
		},
		methods: {
			async initView() {
				this.$store.commit(SET_DETAIL_ITEM, this.propLog);

				if (this.identifier) {
					// load log from db or api to get full image list
					if (this.isPublic) {
						if (!this.watchlist) await this.$store.dispatch(FETCH_WATCHLIST, {hash: this.$route.params.hash});
						if(!this.isDynamicWatchlistItem) {
							// watchlist item fetched through public watchlist link with permalinkHash
							await this.$store.dispatch(FETCH_WATCHLIST_ITEM, {
								watchlistItem: this.identifier,
								hash: this.$route.params.hash
							});
							this.$store.commit(SET_DETAIL_ITEM, this.getConcreteItem(this.watchlistItem));
						} else {
							// watchlist item from dynamic watchlist
							// todo check if the log would appear on this watchlist
							const log = await this.$store.dispatch(FETCH_LOG, this.identifier);
							this.$store.commit(SET_DETAIL_ITEM, log);
						}
					} else if (this.isWatchlistItem) {
						// watchlist item fetched through normal watchlist
						await this.$store.dispatch(FETCH_WATCHLIST_ITEM, {watchlistItem: this.identifier});
            if (!this.watchlist) await this.$store.dispatch(FETCH_WATCHLIST_BY_ITEM, {watchlistItem: this.watchlistItem.uid});
						this.$store.commit(SET_DETAIL_ITEM, this.getConcreteItem(this.watchlistItem));
					} else if (this.isVirtual) {
						// item is a virtual block
						const virtualLog = await this.$store.dispatch(FETCH_VIRTUAL_LOG, this.identifier);
						this.$store.commit(SET_DETAIL_ITEM, virtualLog)
					} else {
						const log = await this.$store.dispatch(FETCH_LOG, this.identifier);
						this.$store.commit(SET_DETAIL_ITEM, log);
					}

					this.updateLogLinks();
				}

				this.initialised = true;
			},
			downloadImages() {
				this.$store.dispatch(DOWNLOAD_MANUAL_IMAGES, {
					veneerLog: this.log.uid,
					watchlistItem: this.watchlistItem
				});
			},
			updateLogLinks() {
				this.updatePreviousLogLink();
				this.updateNextLogLink();
			},
			/**
			 * get link to previous log in list
			 */
			updatePreviousLogLink() {
				if(this.searchType === SEARCH_TYPE.PALLET) {
					return;
				}

				// no previous log in this list
				if (this.listIndex === 0) this.previousLogLink = false;
				this.previousLogLink = this.getLogDetailLink(this.logList[this.listIndex - 1]);
			},
			async updateNextLogLink() {
				if(this.searchType === SEARCH_TYPE.PALLET) {
					return;
				}

				if (!this.isWatchlistItem && this.listIndex === this.logList.length - 1) {
					// todo load new results
					if (!this.logSearchResultsExhausted && !this.loadingLogs) {
						await this.loadAdditionalResults(0);
					}
				}

				this.nextLogLink = this.getLogDetailLink(this.logList[this.listIndex + 1]);
			},
			getLogDetailLink(log) {
				if (!log) return false;

				return {
					name: this.isWatchlistItem ? (this.user ? 'watchlistItemDetail' : 'watchlistItemSharedDetail') : 'detail',
					params: {
						identifier: this.isWatchlistItem ? log.uid : log.intLogId,
						hash: this.isWatchlistItem ? this.watchlist.permalinkHash : "",
						propLog: this.getConcreteItem(log)
					}
				};
			}
		}
	}
</script>

<style lang="scss" scoped>

</style>